import { defineStore } from "pinia";
import { createConsumer } from "@rails/actioncable";

import attachmentsApi from "@/services/api/attachmentsApi";
import conversationsApi from "@/services/api/conversationsApi";
import messagesApi from "@/services/api/messagesApi";

import { getCableUrl } from "@/utils/apiUrlUtils";
import { playChatUnreadSound } from "@/utils/notification/chatSoundUtils";
import {
  chatUnreadDocumentTitle,
  chatUnreadNotificationMessage,
  syncChatUnreadAppBadge,
} from "@/utils/chatUnreadUtils";
import { getCookie } from "@/utils/cookieUtils";
import { getCompanyId } from "@/utils/userUtils";
import { useNotificationStore } from "@/stores/notificationStore";

const CHAT_READ_SYNC_STORAGE_KEY = "chat_read_sync";

export const useChatStore = defineStore("chat", {
  state: () => ({
    companyId: getCompanyId(),
    conversations: [],
    selectedChatId: null,
    messages: [],
    messagesPagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 20,
    },
    dataIsLoading: false,
    messagesAreLoading: false,
    olderMessagesAreLoading: false,
    conversationCable: null,
    messageCable: null,
    chatStorageListener: null,
    conversationSubscriptionIsAvailable: false,
    messageSubscriptionIsAvailable: false,
    retryingMessageIds: {},
  }),

  getters: {
    selectedChat(state) {
      return (
        state.conversations.find(
          (conversation) => conversation.id === state.selectedChatId
        ) || null
      );
    },

    unreadMessagesCount(state) {
      return state.conversations.reduce(
        (total, conversation) =>
          total + Number(conversation.unread_messages_count || 0),
        0
      );
    },

    olderMessagesAreAvailable(state) {
      return Boolean(state.messagesPagination.next);
    },
  },

  actions: {
    async fetchConversations() {
      this.dataIsLoading = true;
      try {
        const response = await conversationsApi.index(this.companyId);
        this.conversations = (response.data.result || []).map(
          (conversation) =>
            this.prepareConversation(conversation, { markAsNew: false })
        );
        this.syncChatUnreadTitle();
      } finally {
        this.dataIsLoading = false;
      }
    },

    async selectChat(id) {
      this.selectedChatId = id;
      this.readChat(id);
      this.messages = [];
      this.resetMessagesPagination();
      await this.fetchMessages();
      await this.markSelectedChatAsRead();
      this.setupMessageSubscription();
    },

    clearSelectedChat() {
      this.selectedChatId = null;
      this.messages = [];
      this.resetMessagesPagination();
      this.messageSubscriptionIsAvailable = false;
      this.disconnectMessageSubscription();
    },

    async fetchMessages() {
      if (!this.selectedChatId) return;

      this.messagesAreLoading = true;
      try {
        const response = await messagesApi.index(
          this.companyId,
          this.selectedChatId
        );
        this.messages = [...(response.data.result || [])].reverse();
        this.applyMessagesPagination(response.data.pagination_meta);
      } finally {
        this.messagesAreLoading = false;
      }
    },

    async loadOlderMessages() {
      if (
        !this.selectedChatId ||
        this.olderMessagesAreLoading ||
        !this.messagesPagination.next
      ) {
        return;
      }

      this.olderMessagesAreLoading = true;
      try {
        const response = await messagesApi.index(
          this.companyId,
          this.selectedChatId,
          { page: this.messagesPagination.next }
        );
        const existingMessageIds = new Set(
          this.messages.map((message) => message.id)
        );
        const olderMessages = [...(response.data.result || [])]
          .reverse()
          .filter((message) => !existingMessageIds.has(message.id));

        this.messages = [...olderMessages, ...this.messages];
        this.applyMessagesPagination(response.data.pagination_meta);
      } finally {
        this.olderMessagesAreLoading = false;
      }
    },

    async sendMessage(payload) {
      if (!this.selectedChatId) return;

      const response = await messagesApi.sendMessage(
        this.companyId,
        this.selectedChatId,
        payload
      );
      this.upsertMessage(response.data.result);
      await this.fetchMessages();
    },

    async retryMessage(message) {
      if (!this.selectedChatId || !message?.id) return;

      this.retryingMessageIds[message.id] = true;
      try {
        const response = await messagesApi.retry(
          this.companyId,
          this.selectedChatId,
          message.id
        );
        this.upsertMessage(response.data.result);
        return response.data.result;
      } finally {
        this.retryingMessageIds[message.id] = false;
      }
    },

    async uploadAttachment(file) {
      const formData = new FormData();
      formData.append("file", file);

      const response = await attachmentsApi.create(
        this.companyId,
        formData,
        true
      );

      return response.data.result;
    },

    async deleteAttachment(attachmentId) {
      await attachmentsApi.delete(this.companyId, attachmentId);
    },

    async markSelectedChatAsRead() {
      const chatId = this.selectedChatId;
      if (!chatId) return;

      const conversation = await this.markChatAsReadOnBackend(chatId);
      this.applyReadConversation(conversation);
      this.readChat(chatId);
    },

    async markChatAsReadOnBackend(chatId) {
      const response = await conversationsApi.markRead(this.companyId, chatId);

      return response.data.result;
    },

    setupConversationSubscription() {
      this.disconnectConversationSubscription();
      this.conversationSubscriptionIsAvailable = false;
      this.conversationCable = createConsumer(getCableUrl(getCookie("token")));
      this.setupChatStorageSync();

      // Список чатов обновляется отдельным каналом без conversation_id:
      // backend присылает разговор целиком, а стор кладет его наверх списка.
      this.conversationCable.subscriptions.create(
        {
          channel: "ConversationChannel",
          company_id: this.companyId,
        },
        {
          received: (data) => {
            this.upsertConversation(data);
          },
          connected: () => {
            this.conversationSubscriptionIsAvailable = true;
            console.info("🔌 Подключены к обновлениям чатов");
          },
          disconnected: () => {
            this.conversationSubscriptionIsAvailable = false;
          },
          rejected: () => {
            this.conversationSubscriptionIsAvailable = false;
            console.error("⚠️ Не удалось подключить обновление чатов");
          },
        }
      );
    },

    setupMessageSubscription() {
      this.disconnectMessageSubscription();
      if (!this.selectedChatId) return;

      this.messageSubscriptionIsAvailable = false;
      this.messageCable = createConsumer(getCableUrl(getCookie("token")));

      // Сообщения подписываются только на выбранный чат: backend требует
      // conversation_id, поэтому при смене чата старую подписку закрываем.
      this.messageCable.subscriptions.create(
        {
          channel: "MessageChannel",
          conversation_id: this.selectedChatId,
        },
        {
          received: (data) => {
            this.upsertMessage(data);
          },
          connected: () => {
            this.messageSubscriptionIsAvailable = true;
            console.info("🔌 Подключены к обновлениям сообщений");
          },
          disconnected: () => {
            this.messageSubscriptionIsAvailable = false;
          },
          rejected: () => {
            this.messageSubscriptionIsAvailable = false;
            console.error("⚠️ Не удалось подключить обновление сообщений");
          },
        }
      );
    },

    disconnectSubscriptions() {
      this.disconnectConversationSubscription();
      this.disconnectMessageSubscription();
    },

    resetState() {
      this.disconnectSubscriptions();
      this.disconnectChatStorageSync();
      this.conversations = [];
      this.selectedChatId = null;
      this.messages = [];
      this.resetMessagesPagination();
      this.syncChatUnreadTitle();
    },

    disconnectConversationSubscription() {
      if (this.conversationCable) {
        this.conversationCable.disconnect();
        this.conversationCable = null;
      }
      this.conversationSubscriptionIsAvailable = false;
    },

    disconnectMessageSubscription() {
      if (this.messageCable) {
        this.messageCable.disconnect();
        this.messageCable = null;
      }
      this.messageSubscriptionIsAvailable = false;
    },

    setupChatStorageSync() {
      if (typeof window === "undefined" || this.chatStorageListener) return;

      this.chatStorageListener = (event) => {
        this.handleChatStorageEvent(event);
      };
      window.addEventListener("storage", this.chatStorageListener);
    },

    disconnectChatStorageSync() {
      if (typeof window === "undefined" || !this.chatStorageListener) return;

      window.removeEventListener("storage", this.chatStorageListener);
      this.chatStorageListener = null;
    },

    async upsertConversation(conversation) {
      const existingIndex = this.conversations.findIndex(
        (item) => item.id === conversation.id
      );
      const previousUnreadCount =
        existingIndex === -1
          ? 0
          : Number(
              this.conversations[existingIndex].unread_messages_count || 0
            );
      const preparedConversation = this.prepareConversation(conversation, {
        markAsNew: existingIndex === -1,
      });
      const unreadCountIncreased =
        Number(preparedConversation.unread_messages_count || 0) >
        previousUnreadCount;

      if (existingIndex !== -1) {
        // Обновленный разговор поднимаем наверх, как в обычных мессенджерах.
        this.conversations.splice(existingIndex, 1);
      }

      this.conversations.unshift(preparedConversation);

      if (this.selectedChatId === conversation.id) {
        // Открытый чат пользователь уже видит на экране, поэтому локальный badge
        // гасим сразу. Backend тоже нужно обновить, иначе после reload это же
        // realtime-сообщение снова вернется как непрочитанное.
        this.readChat(conversation.id);
        await this.markSelectedChatAsRead();
        return;
      }

      this.markChatAsNew(conversation.id);
      this.syncChatUnreadTitle();

      if (unreadCountIncreased) {
        this.notifyUnreadConversation(preparedConversation);
      }
    },

    applyReadConversation(conversation) {
      const existingIndex = this.conversations.findIndex(
        (item) => item.id === conversation.id
      );
      if (existingIndex === -1) return;

      this.conversations.splice(existingIndex, 1, {
        ...this.conversations[existingIndex],
        ...conversation,
      });
      this.syncChatUnreadTitle();
    },

    upsertMessage(message) {
      const existingIndex = this.messages.findIndex(
        (item) => item.id === message.id
      );

      if (existingIndex === -1) {
        this.messages.push(message);
        return;
      }

      this.messages.splice(existingIndex, 1, message);
    },

    applyMessagesPagination(meta) {
      if (!meta) return;

      this.messagesPagination = {
        page: meta.page,
        pages: meta.pages,
        series: meta.series ?? [],
        next: meta.next,
        prev: meta.prev,
        count: meta.count,
        limit: meta.limit,
      };
    },

    resetMessagesPagination() {
      this.messagesPagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 20,
      };
    },

    markChatAsNew(chatId) {
      localStorage.setItem(`new_chat_${chatId}`, "true");
      const conversation = this.conversations.find(
        (item) => item.id === chatId
      );
      if (conversation) conversation.isNew = true;
    },

    readChat(chatId, { broadcast = true } = {}) {
      localStorage.removeItem(`new_chat_${chatId}`);
      const conversation = this.conversations.find(
        (item) => item.id === chatId
      );
      if (conversation) {
        conversation.isNew = false;
        conversation.unread_messages_count = 0;
      }
      this.syncChatUnreadTitle();
      if (broadcast) this.broadcastChatRead(chatId);
    },

    chatMarkedAsNew(chatId) {
      return localStorage.getItem(`new_chat_${chatId}`) === "true";
    },

    handleChatStorageEvent(event) {
      if (event?.key === CHAT_READ_SYNC_STORAGE_KEY) {
        this.applyChatReadStorageSync(event.newValue);
        return;
      }

      const match = String(event?.key || "").match(/^new_chat_(\d+)$/);
      if (!match || event.newValue !== null) return;

      this.readChat(Number(match[1]), { broadcast: false });
    },

    applyChatReadStorageSync(value) {
      if (!value) return;

      try {
        const payload = JSON.parse(value);
        if (!payload?.chatId) return;

        this.readChat(Number(payload.chatId), { broadcast: false });
      } catch {
        // Другие вкладки могут прислать устаревший или поврежденный payload.
      }
    },

    broadcastChatRead(chatId) {
      localStorage.setItem(
        CHAT_READ_SYNC_STORAGE_KEY,
        JSON.stringify({
          chatId: Number(chatId),
          syncedAt: Date.now(),
        })
      );
    },

    prepareConversation(conversation, { markAsNew = false } = {}) {
      const selected = this.selectedChatId === conversation.id;

      return {
        ...conversation,
        unread_messages_count: selected
          ? 0
          : Number(conversation.unread_messages_count || 0),
        isNew: selected
          ? false
          : this.chatMarkedAsNew(conversation.id) || markAsNew,
      };
    },

    notifyUnreadConversation(conversation) {
      useNotificationStore().showInfo(
        chatUnreadNotificationMessage(conversation)
      );
      playChatUnreadSound();
    },

    syncChatUnreadTitle() {
      if (typeof document === "undefined") return;

      const unreadCount = this.unreadMessagesCount;
      document.title = chatUnreadDocumentTitle(
        document.title,
        unreadCount
      );
      syncChatUnreadAppBadge(unreadCount);
    },
  },
});
